/**
 * Bootstrap 5.3's grid breakpoints as media queries, for the handful of places that have to ask about
 * viewport size in TypeScript rather than CSS.
 *
 * These exist because hand-written query strings had drifted from the stylesheet: the layout rendered
 * the mobile bottom nav below `(max-width: 576px)` while every CSS offset written to accommodate that
 * nav used Bootstrap's `media-breakpoint-down(sm)`, which is `(max-width: 575.98px)`.  At exactly 576px
 * the nav appeared with none of its offsets applied, so the quiz banner sat behind it and the chat
 * launcher was not lifted clear (UKC-2924).
 *
 * The 0.02px subtraction is Bootstrap's own: `breakpoint-max()` in _breakpoints.scss returns
 * `$max - .02` so that a `down` query cannot overlap the matching `up` query on fractional widths.
 * Reproduced here so the two stay in step -- change these only if the Sass changes.
 *
 * @see https://getbootstrap.com/docs/5.3/layout/breakpoints/
 */

/** Minimum width of each Bootstrap breakpoint, in px.  Mirrors $grid-breakpoints. */
export const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Everything below the named breakpoint — the equivalent of `media-breakpoint-down($name)`.
 *
 * Note the asymmetry, which is Bootstrap's: `down('sm')` means "smaller than small", i.e. extra-small
 * only. It does not include the small range itself.
 */
export function below(name: Breakpoint): string {
  return `(max-width: ${BREAKPOINTS[name] - 0.02}px)`;
}

/** The named breakpoint and up — the equivalent of `media-breakpoint-up($name)`. */
export function from(name: Breakpoint): string {
  return `(min-width: ${BREAKPOINTS[name]}px)`;
}

/**
 * From one breakpoint up to, but not including, another — `media-breakpoint-between($lower, $upper)`.
 */
export function between(lower: Breakpoint, upper: Breakpoint): string {
  return `${from(lower)} and ${below(upper)}`;
}
